import { getEvent, race, type EventId, type PriceTier, type RaceEvent } from './race.config'

export type Coupon = {
  code: string
  kind: 'percent' | 'amount'
  /** Percent (0–100) or cents off each registration. */
  value: number
  label: string
  appliesTo?: EventId[]
  active?: boolean
  maxUses?: number | null
  uses?: number
  expiresAt?: string | null
}

export type QuoteLine = {
  kind: 'registration' | 'shirt' | 'donation' | 'discount' | 'fee'
  label: string
  detail?: string
  cents: number
  participantIndex?: number
}

export type Quote = {
  lines: QuoteLine[]
  registrationCents: number
  shirtsCents: number
  discountCents: number
  donationCents: number
  feeCents: number
  totalCents: number
  coupon?: Coupon
}

export type QuoteInput = {
  participants: { eventId: EventId; firstName?: string; lastName?: string }[]
  extraShirts?: { fit: string; size: string; qty: number }[]
  donationCents?: number
}

/** The tier in effect at `now`, plus the one after it (for "price goes up on…" messaging). */
export function currentTier(event: RaceEvent, now = new Date()): { tier: PriceTier; next?: PriceTier; endsAt?: string } {
  const t = now.getTime()
  const idx = event.tiers.findIndex((tier) => !tier.endsAt || t <= new Date(tier.endsAt).getTime())
  const i = idx === -1 ? event.tiers.length - 1 : idx
  return { tier: event.tiers[i], next: event.tiers[i + 1], endsAt: event.tiers[i].endsAt }
}

export function priceFor(eventId: EventId, now = new Date()) {
  const event = getEvent(eventId)
  if (!event) throw new Error(`Unknown event ${eventId}`)
  return currentTier(event, now).tier.cents
}

/** The fee to add so that, after Stripe takes percent + fixed, baseCents is left. */
export function grossUpFee(baseCents: number) {
  if (baseCents <= 0) return 0
  const { percent, fixedCents } = race.processingFee
  const gross = Math.ceil((baseCents + fixedCents) / (1 - percent / 100))
  return gross - baseCents
}

export function processingFee(baseCents: number) {
  return race.processingFee.mode === 'absorb' ? 0 : grossUpFee(baseCents)
}

/** Totals for a gift on the Donate page; the donor chooses whether to cover the card fee. */
export function donationTotals(amountCents: number, coverFee: boolean) {
  const feeCents = coverFee ? grossUpFee(amountCents) : 0
  return { amountCents, feeCents, totalCents: amountCents + feeCents }
}

export function couponDiscount(coupon: Coupon | undefined, eventId: EventId, priceCents: number) {
  if (!coupon || coupon.active === false) return 0
  if (coupon.appliesTo?.length && !coupon.appliesTo.includes(eventId)) return 0
  const off = coupon.kind === 'percent' ? Math.round((priceCents * coupon.value) / 100) : coupon.value
  return Math.min(priceCents, Math.max(0, off))
}

export function couponIsUsable(coupon: Coupon | undefined, now = new Date()) {
  if (!coupon || coupon.active === false) return false
  if (coupon.expiresAt && now.getTime() > new Date(coupon.expiresAt).getTime()) return false
  if (coupon.maxUses != null && (coupon.uses ?? 0) >= coupon.maxUses) return false
  return true
}

/**
 * Price an order. Runs in the browser for the live summary and again on the
 * server, which is the only total Stripe ever sees.
 */
export function quote(input: QuoteInput, coupon?: Coupon, now = new Date()): Quote {
  const lines: QuoteLine[] = []
  let registrationCents = 0
  let discountCents = 0
  const usable = couponIsUsable(coupon, now) ? coupon : undefined

  input.participants.forEach((p, i) => {
    const event = getEvent(p.eventId)
    if (!event) return
    const { tier } = currentTier(event, now)
    const who = [p.firstName, p.lastName].filter(Boolean).join(' ') || `Participant ${i + 1}`
    lines.push({
      kind: 'registration',
      label: event.name,
      detail: event.tiers.length > 1 ? `${who} · ${tier.label} price` : who,
      cents: tier.cents,
      participantIndex: i,
    })
    registrationCents += tier.cents
    const off = couponDiscount(usable, event.id, tier.cents)
    if (off) discountCents += off
  })

  let shirtsCents = 0
  for (const s of input.extraShirts ?? []) {
    if (!s.qty) continue
    const cents = s.qty * race.extras.extraShirtCents
    shirtsCents += cents
    const fit = race.shirts.fits.find((f) => f.id === s.fit)?.label ?? s.fit
    lines.push({ kind: 'shirt', label: `Extra race shirt × ${s.qty}`, detail: `${fit} ${s.size}`, cents })
  }

  if (discountCents && usable) {
    lines.push({ kind: 'discount', label: `Code ${usable.code}`, detail: usable.label, cents: -discountCents })
  }

  const donationCents = Math.max(0, Math.round(input.donationCents ?? 0))
  if (donationCents) lines.push({ kind: 'donation', label: 'Donation', detail: `For ${race.cause}`, cents: donationCents })

  const base = registrationCents - discountCents + shirtsCents + donationCents
  const feeCents = processingFee(base)
  if (feeCents) lines.push({ kind: 'fee', label: 'Card processing', detail: 'Covers Stripe’s fee so the race keeps every dollar', cents: feeCents })

  return {
    lines,
    registrationCents,
    shirtsCents,
    discountCents,
    donationCents,
    feeCents,
    totalCents: base + feeCents,
    coupon: discountCents ? usable : undefined,
  }
}

/** Unambiguous code alphabet (no 0/O, 1/I/L). */
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'
export function confirmationCode(random: (n: number) => Uint8Array) {
  const bytes = random(6)
  let code = ''
  for (const b of bytes) code += ALPHABET[b % ALPHABET.length]
  return `${race.codePrefix}-${code}`
}
